import domtoimage from 'dom-to-image';
import { QRCodeSVG } from 'qrcode.react';
import React, { FC, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import { NOTIFICATION_TYPES, SharePostLinksEnum } from 'src/common/constants';
import { copyToClipboard, currencyFormatter } from 'src/common/helpers';
import { Modal, ModalTitle, ModalContent, Input, TextArea } from 'src/components/core';
import {
    Copy,
    CopyLink,
    Cross,
    Download,
    Email,
    Facebook,
    Linkedin,
    PnLLoss,
    PnLProfit,
    Telegram,
    Twitter,
    Whatsapp,
} from 'src/components/core/icons';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';
import { BASE_URL, COINFLARE_REGISTER_URL } from 'src/config';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { showNotification } from 'src/setup/redux/slices/derivativesSlice';
import { ToastType, Position } from 'src/types';

interface ShareProfitLossModalProps {
    open: boolean;
    position: Position;
    isActive: boolean;
    handleClose: () => void;
}

const StyledModal = styled(Modal)`
    ${({ theme: { colors, typography } }) => `
        .modal-paper {
            width: 779px;
            background: ${colors.background.primary};
        }
        .modal-title {
            padding: 12px 24px;
            border-bottom: 1px solid ${colors.border.light};
            > div {
                font-size: ${typography.size.desktop.paragraph.p1};
                line-height: ${typography.lineHeight[7]};
                font-weight: ${typography.weight.semiBold};
            }
        }
        .modal-content {
            padding: 24px;
            overflow: hidden;
            font-size: ${typography.size.desktop.paragraph.p2};
        }
    `}
`;

const TitleText = styled.div`
    ${({ theme: { colors } }) => `
        color: ${colors.text.primary};
        display: inline-block;
    `}
`;

const ImageContainer = styled.div`
    width: 343px;
    height: 429px;
    position: relative;
`;

const PnLAnalysisContainer = styled(FlexContainer)`
    position: absolute;
    top: 53px;
    left: 19px;
`;

const TokenName = styled.span`
    ${({ theme: { colors, typography } }) => `
        font-weight: ${typography.weight.medium};
        line-height: ${typography.lineHeight[7]};
        color: ${colors.text.primary};
    `}
`;

const RoiContainer = styled(FlexContainer)`
    gap: 12.5px;
    ${({ theme: { colors, typography } }) => `
        font-size: ${typography.size.desktop.paragraph.p3};
        line-height: ${typography.lineHeight[3]};
        color: ${colors.text.tertiary};
    `}
`;

const PositionSide = styled.span<{ isLong: number }>`
    padding: 4px 8px;
    border-radius: 25px;
    ${({ theme: { colors }, isLong }) => `
         background: ${
             isLong
                 ? colors.palette.background.overlay.green
                 : colors.palette.background.overlay.red
         };
        color: ${isLong ? colors.alert.green : colors.alert.red};
    `}
`;

const PositionLeverage = styled.span`
    ${({ theme: { colors } }) => `
        color: ${colors.alert.yellow};
    `}
`;

const PositionPnLPercentage = styled.span<{ color: string }>`
    ${({ theme: { typography }, color }) => `
        color: ${color};
        font-size: ${typography.size.desktop.heading.h3};
        line-height: ${typography.lineHeight[14]};
        font-weight: ${typography.weight.medium};
    `}
`;

const PricesContainer = styled(FlexContainer)`
    flex-direction: column;
    align-items: flex-start;
    gap: 3px;
    & span {
        ${({ theme: { colors, typography } }) => `
            &:nth-child(1) {
                color: ${colors.text.tertiary};
                font-size: ${typography.size.desktop.paragraph.p3};
                letter-spacing: 0.2px;
            }
            &:nth-child(2) {
                color: ${colors.palette.white};
                font-size: ${typography.size.desktop.paragraph.p2};
                line-height: ${typography.lineHeight[5]};
                font-weight: ${typography.weight.medium};
            }
        `}
    }
`;

const ReferralContainer = styled(FlexContainer)`
    position: absolute;
    left: 16px;
    bottom: 7px;
`;

const ReferralDescription = styled.span`
    ${({ theme: { colors, typography } }) => `
        color: ${colors.text.accent};
        font-size: ${typography.size.desktop.paragraph.p3};
        line-height: ${typography.lineHeight[3]};
    `}
`;

const ReferralCode = styled.span`
    ${({ theme: { colors, typography } }) => `
        color: ${colors.text.accent};
        font-size: ${typography.size.desktop.paragraph.p2};
        line-height: ${typography.lineHeight[4]};
        font-weight: ${typography.weight.semiBold};
    `}
`;

const ShareContainer = styled(FlexContainer)`
    width: 348px;
    flex-direction: column;
    gap: 22px;
`;

const ReferralInputContainer = styled(FlexContainer)`
    flex-direction: column;
    gap: 6px;
    align-items: start;
    width: 100%;
`;

const ReferralTitleText = styled.div`
    ${({ theme: { typography } }) => `
        font-size: ${typography.size.desktop.paragraph.p3};
        line-height: ${typography.lineHeight[3]}
        ;
    `}
`;

const SocialMediaContainer = styled(FlexContainer)`
    gap: 8px;
    width: 100%;
    flex-wrap: wrap;
`;

const SocialMediaItem = styled(FlexContainer)`
    flex-direction: column;
    gap: 4px;
    text-align: center;
    width: 60px;
    cursor: pointer;
`;

const SocialMediaItemText = styled.div`
    ${({ theme: { colors, typography } }) => `
        color: ${colors.text.tertiary};
        font-size: ${typography.size.mobile.paragraph.p3};
        letter-spacing: 0.2px;
        width: 100%;
    `}
`;

const ShareProfitLossModal: FC<ShareProfitLossModalProps> = ({
    open,
    position,
    isActive,
    handleClose,
}) => {
    const { t } = useTranslation();
    const { colors } = useTheme();
    const ref = useRef<HTMLDivElement | null>(null);

    const [inviteDescription, setInviteDescription] = useState(
        t('share_pl_modal_referral_description'),
    );

    const dispatch = useAppDispatch();

    const { userInviteInfo } = useAppSelector((state) => state.user);

    const handleCancel = () => {
        handleClose();
    };

    const copyReferralCodeLink = (value: string, message: string) => {
        copyToClipboard(value);
        dispatch(
            showNotification({
                title: message,
                type: NOTIFICATION_TYPES.SUCCESS as ToastType,
            }),
        );
    };

    const onSaveImageClick = useCallback(() => {
        if (ref.current === null) {
            return;
        }

        domtoimage
            .toPng(ref.current, { cacheBust: true })
            .then((dataUrl) => {
                const link = document.createElement('a');
                link.download = 'my-pnl.png';
                link.href = dataUrl;
                link.click();
            })
            .catch((err) => {
                console.log(err);
            });
    }, [ref]);

    return (
        <StyledModal size="md" open={open}>
            <ModalTitle>
                <TitleText>{t('share_pl_modal_title')}</TitleText>
                <Cross
                    fill={colors.text.primary}
                    onClick={handleCancel}
                    className="clickable"
                    style={{ height: 20, width: 20 }}
                />
            </ModalTitle>
            <ModalContent>
                <FlexContainer
                    styles={{
                        gap: 40,
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                    }}
                >
                    <ImageContainer ref={ref}>
                        {Number(position.profitPercentage) > 0 ? <PnLProfit /> : <PnLLoss />}
                        <PnLAnalysisContainer>
                            <FlexContainer
                                styles={{
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    gap: 2,
                                }}
                            >
                                <TokenName>{position.symbolName}</TokenName>
                                <RoiContainer>
                                    <span>{t('roi')}</span>
                                    <PositionSide isLong={Number(position.isLong)}>
                                        {t(Number(position.isLong) ? 'long' : 'short')}
                                    </PositionSide>
                                    <PositionLeverage>{`${t('leverage')} ${
                                        position.leverage
                                    }x`}</PositionLeverage>
                                </RoiContainer>
                                <FlexContainer
                                    styles={{
                                        flexDirection: 'column',
                                        gap: 24,
                                        alignItems: 'flex-start',
                                    }}
                                >
                                    <PositionPnLPercentage
                                        color={
                                            Number(position.profitPercentage) > 0
                                                ? colors.alert.green
                                                : colors.alert.red
                                        }
                                    >
                                        {`${
                                            Number(position.profitPercentage) > 0 ? '+' : ''
                                        }${currencyFormatter(
                                            Number(position.profitPercentage),
                                            2,
                                        )}%`}
                                    </PositionPnLPercentage>
                                    <FlexContainer styles={{ gap: 23 }}>
                                        <PricesContainer>
                                            <span>{t('entry_price')}</span>
                                            <span>{position.avgPrice}</span>
                                        </PricesContainer>
                                        <PricesContainer>
                                            <span>
                                                {t(isActive ? 'market_price' : 'exit_price')}
                                            </span>
                                            <span>{isActive ? position.lastPrice : ''}</span>
                                        </PricesContainer>
                                    </FlexContainer>
                                </FlexContainer>
                            </FlexContainer>
                        </PnLAnalysisContainer>
                        <ReferralContainer>
                            <FlexContainer styles={{ width: 266, paddingRight: 12 }}>
                                <FlexContainer
                                    styles={{
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        marginRight: 30,
                                    }}
                                >
                                    <ReferralDescription>
                                        {t('share_pl_modal_referral_description')}
                                    </ReferralDescription>
                                    <ReferralCode>{`${t('share_pl_modal_referral_code_title')}: ${
                                        userInviteInfo.inviteCode
                                    }`}</ReferralCode>
                                </FlexContainer>
                            </FlexContainer>
                            <FlexContainer>
                                <QRCodeSVG
                                    size={45}
                                    value={`${COINFLARE_REGISTER_URL}/${userInviteInfo.inviteCode}`}
                                />
                            </FlexContainer>
                        </ReferralContainer>
                    </ImageContainer>
                    <ShareContainer>
                        <ReferralInputContainer>
                            <ReferralTitleText>
                                {t('share_pl_modal_referral_code_title')}
                            </ReferralTitleText>
                            <Input
                                name="referral_code"
                                hasTooltip={false}
                                value={userInviteInfo.inviteCode}
                                rightElement={
                                    <Copy
                                        fill={colors.text.tertiary}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => {
                                            copyReferralCodeLink(
                                                userInviteInfo.inviteCode,
                                                'copied',
                                            );
                                        }}
                                    />
                                }
                            />
                        </ReferralInputContainer>
                        <TextArea
                            name={'description'}
                            fullWidth
                            label={t('share_pl_modal_referral_description_title')}
                            value={inviteDescription}
                            rows={4}
                            onChange={(value: string) => {
                                setInviteDescription(value);
                            }}
                        />
                        <SocialMediaContainer>
                            <SocialMediaItem onClick={onSaveImageClick}>
                                <Download
                                    fill={colors.background.secondary.v4}
                                    stroke={colors.text.primary}
                                />
                                <SocialMediaItemText>{t('save_image')}</SocialMediaItemText>
                            </SocialMediaItem>
                            <SocialMediaItem
                                onClick={() => {
                                    copyReferralCodeLink(
                                        `${COINFLARE_REGISTER_URL}/${userInviteInfo.inviteCode}`,
                                        'copied',
                                    );
                                }}
                            >
                                <CopyLink
                                    fill={colors.background.secondary.v4}
                                    stroke={colors.text.primary}
                                />
                                <SocialMediaItemText>{t('copy_link')}</SocialMediaItemText>
                            </SocialMediaItem>
                            <SocialMediaItem
                                onClick={() => {
                                    window.open(
                                        `mailto:?subject=${encodeURIComponent(
                                            'Share',
                                        )}&body=${encodeURIComponent(inviteDescription)}`,
                                        '_blank',
                                    );
                                }}
                            >
                                <Email
                                    fill={colors.background.secondary.v4}
                                    stroke={colors.text.primary}
                                />
                                <SocialMediaItemText>{t('email')}</SocialMediaItemText>
                            </SocialMediaItem>
                            <SocialMediaItem
                                onClick={() => {
                                    window.open(
                                        `${SharePostLinksEnum.TWITTER}text=${encodeURIComponent(
                                            inviteDescription,
                                        )}&url=${encodeURIComponent(BASE_URL)}`,
                                        '_blank',
                                    );
                                }}
                            >
                                <Twitter />
                                <SocialMediaItemText>{t('twitter')}</SocialMediaItemText>
                            </SocialMediaItem>
                            <SocialMediaItem
                                onClick={() => {
                                    window.open(
                                        `${SharePostLinksEnum.TELEGRAM}&url=${encodeURIComponent(
                                            BASE_URL,
                                        )}&text=${encodeURIComponent(inviteDescription)}`,
                                        '_blank',
                                    );
                                }}
                            >
                                <Telegram />
                                <SocialMediaItemText>{t('telegram')}</SocialMediaItemText>
                            </SocialMediaItem>
                            <SocialMediaItem
                                onClick={() => {
                                    window.open(
                                        `${SharePostLinksEnum.FACEBOOK}u=${encodeURIComponent(
                                            BASE_URL,
                                        )}`,
                                        '_blank',
                                    );
                                }}
                            >
                                <Facebook />
                                <SocialMediaItemText>{t('facebook')}</SocialMediaItemText>
                            </SocialMediaItem>
                            <SocialMediaItem
                                onClick={() => {
                                    window.open(
                                        `${SharePostLinksEnum.LINKEDIN}url=${BASE_URL}`,
                                        '_blank',
                                    );
                                }}
                            >
                                <Linkedin />
                                <SocialMediaItemText>{t('linkedin')}</SocialMediaItemText>
                            </SocialMediaItem>
                            <SocialMediaItem
                                onClick={() => {
                                    window.open(
                                        `${SharePostLinksEnum.WHATSAPP}text=${encodeURIComponent(
                                            inviteDescription,
                                        )} ${encodeURIComponent(BASE_URL)}`,
                                        '_blank',
                                    );
                                }}
                            >
                                <Whatsapp />
                                <SocialMediaItemText>{t('whatsapp')}</SocialMediaItemText>
                            </SocialMediaItem>
                        </SocialMediaContainer>
                    </ShareContainer>
                </FlexContainer>
            </ModalContent>
        </StyledModal>
    );
};

export { ShareProfitLossModal };
